/**
 * Add Technician dialog — search Entra users and assign technician attributes.
 * Admin-only. Uses Command (cmdk) for search with attribute dropdowns.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { X, Search, UserPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/sc/glass-card";
import {
  useEntraUserSearch,
  useUpdateTechnicianAttributes,
} from "@/hooks/use-technicians";
import { TechnicianAttributeForm } from "./technician-attribute-form";
import type {
  EntraUserSearchResult,
  TechnicianAttributeUpdate,
} from "@/types";

interface AddTechnicianDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function AddTechnicianDialog({ open, onClose }: AddTechnicianDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<EntraUserSearchResult | null>(null);
  const [attributes, setAttributes] = useState<TechnicianAttributeUpdate>({
    technicianTier: "Tier-2",
    securityClearance: "None",
    workLocationCountry: "AU",
  });
  const backdropRef = useRef<HTMLDivElement>(null);

  const searchResults = useEntraUserSearch(searchQuery);
  const updateMutation = useUpdateTechnicianAttributes();

  const handleClose = useCallback(() => {
    setSearchQuery("");
    setSelectedUser(null);
    setAttributes({
      technicianTier: "Tier-2",
      securityClearance: "None",
      workLocationCountry: "AU",
    });
    onClose();
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, handleClose]);

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !attributes.technicianTier) return;

    try {
      await updateMutation.mutateAsync({
        userId: selectedUser.userId,
        attributes,
      });
      handleClose();
    } catch {
      // Error state handled by updateMutation.isError
    }
  }, [selectedUser, attributes, updateMutation, handleClose]);

  if (!open) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === backdropRef.current) handleClose();
      }}
    >
      <GlassCard className="w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserPlus size={20} className="text-sc-primary" />
            <h2 className="text-h3 font-display text-sc-navy">
              Add Technician
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-btn text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {!selectedUser ? (
          /* Step 1: Search for user */
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-widest block mb-2">
              Search Entra ID Users
            </label>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-sc-text-muted"
              />
              <input
                autoFocus
                placeholder="Start typing a name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-10 w-full rounded-input border border-sc-border bg-sc-input-bg pl-9 pr-3 text-body-sm text-sc-text placeholder:text-sc-text-muted"
              />
            </div>

            {searchQuery.length >= 2 && (
              <div className="mt-2 border border-sc-border rounded-input max-h-60 overflow-y-auto">
                {searchResults.isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 size={20} className="animate-spin text-sc-text-muted" />
                  </div>
                ) : searchResults.data?.length === 0 ? (
                  <p className="text-body-sm text-sc-text-muted text-center py-4">
                    No users found
                  </p>
                ) : (
                  searchResults.data?.map((user) => (
                    <button
                      key={user.userId}
                      onClick={() => setSelectedUser(user)}
                      className="w-full text-left px-4 py-3 hover:bg-sc-surface-hover transition-colors border-b border-sc-border/50 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-body-sm text-sc-text font-medium">
                            {user.displayName}
                          </p>
                          <p className="text-label text-sc-text-muted">
                            {user.userPrincipalName}
                          </p>
                        </div>
                        {user.isTechnician && (
                          <span className="text-label text-sc-primary bg-sc-primary-light px-2 py-0.5 rounded-full">
                            Already a technician
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <p className="text-label text-sc-text-muted mt-2">
                Type at least 2 characters to search
              </p>
            )}
          </div>
        ) : (
          /* Step 2: Assign attributes */
          <div>
            <div className="mb-4 p-3 rounded-input bg-sc-surface border border-sc-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-body-sm text-sc-text font-medium">
                    {selectedUser.displayName}
                  </p>
                  <p className="text-label text-sc-text-muted">
                    {selectedUser.userPrincipalName}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-label text-sc-primary hover:underline"
                >
                  Change
                </button>
              </div>
            </div>

            <TechnicianAttributeForm
              attributes={attributes}
              onChange={setAttributes}
            />

            {updateMutation.isError && (
              <p className="text-label text-sc-danger mt-3">
                {(updateMutation.error as Error).message ?? "Failed to save"}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={!attributes.technicianTier || updateMutation.isPending}
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Add Technician"
                )}
              </Button>
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
